import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  BulkOperationsState,
  BulkOperationItem,
  BulkOperationMatch,
} from './types';

/**
 * A pending operation is identified by company + protected asset + threat, not
 * by threat id alone. The threat list shows the same threat once per
 * company/asset pair, so keying this map by `threatId` let one row's operation
 * overwrite its sibling's - leaving the sibling visible and re-actionable,
 * which is the bug this map is supposed to prevent. WHEEL-359.
 */
export const bulkOperationKey = (
  companyId: number | string | undefined,
  domainId: number | string | undefined,
  threatId: number | string | undefined,
) => `${companyId}_${domainId}_${threatId}`;

/** Same key, derived from a threat-list row or a WebSocket payload. */
export const bulkOperationKeyForThreat = (threat: {
  company_id?: number | string;
  protected_asset_id?: number | string;
  threat_id?: number | string;
}) =>
  bulkOperationKey(
    threat.company_id,
    threat.protected_asset_id,
    threat.threat_id,
  );

/**
 * Can `match` name a single operation? Needs both which threat (id or name) and
 * whose (company or asset) - the same threat id and the same domain each appear
 * once per company/asset pair, so either half alone would resolve a sibling's
 * operation and un-suppress a row the analyst never actioned. WHEEL-359.
 */
const namesOneOperation = (match: BulkOperationMatch) =>
  (match.threatId != null || !!match.threatName) &&
  (match.companyId != null || match.domainId != null);

const matchesOperation = (
  operation: BulkOperationItem,
  match: BulkOperationMatch,
) => {
  if (match.companyId != null && operation.companyId !== match.companyId) {
    return false;
  }
  if (match.domainId != null && operation.domainId !== match.domainId) {
    return false;
  }
  // With an id, the id decides. `threatName` is deliberately NOT cross-checked:
  // the WebSocket sends a bare host while an operation's `threatName` came from
  // the list row, which can be the full URL including path. Comparing them left
  // the operation unresolved and the row suppressed until its TTL. WHEEL-359.
  if (match.threatId != null) return operation.threatId === match.threatId;
  return operation.threatName === match.threatName;
};

/**
 * How long a resolved operation keeps its row filtered out. Only has to outlive
 * a list request that was already in flight when the confirmation arrived.
 */
export const RESOLVED_GRACE_MS = 60_000;

const initialState: BulkOperationsState = {
  pendingOperations: {},
  resolvedOperations: {},
};

const bulkOperationsSlice = createSlice({
  name: 'bulkOperations',
  initialState,
  reducers: {
    addBulkOperation: (state, action: PayloadAction<BulkOperationItem[]>) => {
      action.payload.forEach(operation => {
        state.pendingOperations[
          bulkOperationKey(
            operation.companyId,
            operation.domainId,
            operation.threatId,
          )
        ] = operation;
      });
    },
    /**
     * Resolves every pending operation matching the payload: drops it from
     * `pendingOperations` (so the pending badge clears) and records it in
     * `resolvedOperations` so its row stays filtered for the grace period.
     *
     * Takes a match rather than a key so a degraded WebSocket payload
     * (threat_id and company_id are both optional on
     * ThreatStatusUpdatePayload) can still clear its operation. A payload
     * carrying no identifier at all is ignored instead of clearing the map.
     *
     * `resolvedAt` comes from the caller rather than `new Date()` so the
     * reducer stays pure.
     */
    removeBulkOperation: (
      state,
      action: PayloadAction<BulkOperationMatch & { resolvedAt: string }>,
    ) => {
      const { resolvedAt, ...match } = action.payload;
      // Too vague to act on: leave the operation pending and let its TTL expire
      // rather than resolving a sibling's.
      if (!namesOneOperation(match)) return;

      Object.keys(state.pendingOperations).forEach(key => {
        if (matchesOperation(state.pendingOperations[key], match)) {
          delete state.pendingOperations[key];
          state.resolvedOperations[key] = resolvedAt;
        }
      });

      // Keep the map bounded: a batch of 100 adds 100 entries, and a long
      // analyst session runs many batches.
      const cutoff = new Date(resolvedAt).getTime() - RESOLVED_GRACE_MS;
      Object.keys(state.resolvedOperations).forEach(key => {
        const at = new Date(state.resolvedOperations[key]).getTime();
        if (Number.isNaN(at) || at < cutoff) {
          delete state.resolvedOperations[key];
        }
      });
    },
    setBulkOperationError: (
      state,
      action: PayloadAction<{
        key: string;
        errorMessage: string;
      }>,
    ) => {
      const operation = state.pendingOperations[action.payload.key];
      if (operation) {
        operation.hasError = true;
        operation.errorMessage = action.payload.errorMessage;
      }
    },
    /**
     * Dismisses the pending list from the Header drawer. Deliberately leaves
     * `resolvedOperations` alone - clearing it would put confirmed rows back on
     * screen.
     */
    clearAllOperations: state => {
      state.pendingOperations = {};
    },
  },
});

export const {
  addBulkOperation,
  removeBulkOperation,
  setBulkOperationError,
  clearAllOperations,
} = bulkOperationsSlice.actions;

export default bulkOperationsSlice.reducer;
